import React from 'react';
import { FiMenu, FiBell, FiHelpCircle } from 'react-icons/fi';

// Types
interface QNavHeaderProps {
  toggleSidebar: () => void;
  sidebarOpen: boolean;
  user: any;
}

const QNavHeader: React.FC<QNavHeaderProps> = ({ toggleSidebar, sidebarOpen, user }) => {
  const [notifications] = React.useState<number>(2); // For future use
  const [showHelp, setShowHelp] = React.useState<boolean>(false);

  return (
    <header className="bg-brand-gradient text-white border-b border-secondary-300 py-3 px-4 flex items-center justify-between shadow-md">
      {/* Left side - Menu toggle and branding */}
      <div className="flex items-center">
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-qnav text-white hover:bg-secondary-700/30 focus:outline-none focus:ring-2 focus:ring-secondary-400"
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          <FiMenu className="h-5 w-5" />
        </button>

        <div className="ml-4 flex items-center">
          <div className="h-8 w-8 rounded bg-brand-orange flex items-center justify-center mr-2">
            <span className="text-brand-blue font-bold">Q</span>
          </div>
          <span className="font-display font-bold text-xl text-white">Q-Navigator</span>
          <span className="ml-1 text-xs bg-brand-orange text-brand-blue px-1.5 py-0.5 rounded-sm font-bold">Beta</span>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center space-x-4">
        {/* Help button */}
        <div className="relative">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 rounded-qnav text-white hover:bg-secondary-700/30 focus:outline-none focus:ring-2 focus:ring-secondary-400"
            aria-label="Help"
            aria-expanded={showHelp}
          >
            <FiHelpCircle className="h-5 w-5" />
          </button>

          {showHelp && (
            <div className="absolute right-0 mt-2 w-72 bg-surface rounded-qnav shadow-brand-card border border-secondary-200 z-10 qnav-fade-in">
              <div className="p-4">
                <h3 className="font-semibold text-secondary-800 mb-2">Q-Navigator Help</h3>
                <p className="text-sm text-secondary-600 mb-3">
                  Get assistance with your Q Developer adoption journey using our intelligent tools.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => window.open('https://quip-amazon.com/BzoqAeA3mUg8/How-to-Guide', '_blank')}
                    className="block text-sm text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    View Documentation
                  </button>
                  <button className="block text-sm text-primary-600 hover:text-primary-700 hover:underline">
                    Watch Tutorial
                  </button>
                  <button 
                    onClick={() => {
                      const subject = encodeURIComponent('Q-Navigator Support Request');
                      const body = encodeURIComponent('Hello Q-Navigator Support Team,\n\n[Please describe your issue or question here]\n\nThank you!');
                      window.location.href = `mailto:q-csx+q-navigator-prod@amazon.com?subject=${subject}&body=${body}`;
                    }}
                    className="block text-sm text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            className="p-2 rounded-qnav text-white hover:bg-secondary-700/30 focus:outline-none focus:ring-2 focus:ring-secondary-400"
            aria-label={`${notifications} notifications`}
          >
            <FiBell className="h-5 w-5" />
            {notifications > 0 && (
              <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-error-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {notifications}
              </span>
            )}
          </button>
        </div>

        {/* User profile */}
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-brand-anchor flex items-center justify-center text-white font-medium">
            {user?.attributes?.name ? user.attributes.name.charAt(0) : 'U'}
          </div>
          <div className="ml-2 hidden md:block">
            <div className="text-sm font-medium text-white">{user?.attributes?.name || user?.username || 'User'}</div>
            <div className="text-xs text-secondary-300">{user?.attributes?.email || ''}</div>
          </div>
        </div>
      </div>
    </header>
  );
};

export { QNavHeader };
